import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, MapPin, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ['Editorial Commission', 'Advertising Campaign', 'Architectural Documentation', 'Private Collection', 'Publication'];
const BUDGETS = ['< €3,000', '€3,000 – €8,000', '€8,000 – €20,000', '€20,000+', 'Let\'s discuss'];

export default function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', project_type: '', timeline: '', budget: '', message: '' });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please enter your name and email.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/inquiry`, form);
      toast.success(res.data.message || 'Inquiry received. Elena will be in touch soon.');
      setForm({ name: '', email: '', project_type: '', timeline: '', budget: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#FDFCF7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-neutral-400 mb-2">Inquiries</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#121212] leading-tight">
            Start a Conversation
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-16 lg:gap-24">

          {/* Form */}
          <motion.form
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-2">Name</label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Your full name"
                  className="input-underline"
                  required
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-2">Email</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="your@email.com"
                  className="input-underline"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-2">Commission Type</label>
                <select
                  data-testid="contact-input-type"
                  value={form.project_type}
                  onChange={set('project_type')}
                  className="input-underline"
                >
                  <option value="">Select type...</option>
                  {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-2">Budget Range</label>
                <select
                  data-testid="contact-input-budget"
                  value={form.budget}
                  onChange={set('budget')}
                  className="input-underline"
                >
                  <option value="">Select range...</option>
                  {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-2">Project Notes</label>
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={set('message')}
                placeholder="Tell me about your project, vision, and timeline..."
                rows={4}
                className="input-underline resize-none"
              />
            </div>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="bg-[#121212] text-white rounded-full px-10 py-4 text-sm font-medium tracking-wide hover:bg-neutral-700 active:scale-[0.98] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Inquiry'}
            </button>
          </motion.form>

          {/* Studio Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="space-y-8"
          >
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-4">Studio Contact</p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail size={14} strokeWidth={1.25} className="text-neutral-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-light text-[#121212]">studio@elenavance.studio</p>
                    <p className="text-[11px] font-mono text-neutral-400">General inquiries</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={14} strokeWidth={1.25} className="text-neutral-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-light text-[#121212]">+33 (0)1 42 68 00 00</p>
                    <p className="text-[11px] font-mono text-neutral-400">M+A Reps Paris</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={14} strokeWidth={1.25} className="text-neutral-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-light text-[#121212]">7e Arrondissement, Paris</p>
                    <p className="text-[11px] font-mono text-neutral-400">By appointment</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-black/[0.06]">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-3">Represented by</p>
              <p className="font-serif text-lg font-light text-[#121212]">M+A Reps</p>
              <p className="text-xs font-mono text-neutral-400">Paris · New York · Tokyo</p>
            </div>

            <div className="pt-4">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-400 mb-2">Response time</p>
              <p className="text-sm font-light text-neutral-600">Within 48 hours for all inquiries. Rush commissions available upon discussion.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
